/**
 * Resolves the current admin user identity.
 *
 * Priority order:
 * 1. Kinde user (set by AdminAuthProvider via window.__KINDE_USER__)
 * 2. Cloudflare Access header (legacy — `window.CF_ACCESS_USER`)
 * 3. Fallback placeholder for local dev / Storybook.
 */

const FALLBACK_EMAIL = "you@example";

type CfAccessUserGlobal = { CF_ACCESS_USER?: string };
type KindeUserGlobal = { __KINDE_USER__?: { email?: string; given_name?: string } };

const buildInitials = (email: string): string => {
  const localPart = email.split("@")[0] ?? "";
  const tokens = localPart.split(/[._-]+/).filter((part) => part.length > 0);
  if (tokens.length === 0) return "?";
  if (tokens.length === 1) return tokens[0]!.slice(0, 2).toUpperCase();
  return (tokens[0]![0]! + tokens[1]![0]!).toUpperCase();
};

export type CurrentUser = {
  readonly email: string;
  readonly initials: string;
  readonly provider: "kinde" | "cloudflare_access" | "fallback";
};

export const resolveCurrentUser = (): CurrentUser => {
  if (typeof window === "undefined") {
    return { email: FALLBACK_EMAIL, initials: "?", provider: "fallback" };
  }

  // Kinde user (set from RequireAuth context)
  const kindeUser = (window as unknown as KindeUserGlobal).__KINDE_USER__;
  if (kindeUser?.email) {
    return {
      email: kindeUser.email,
      initials: buildInitials(kindeUser.email),
      provider: "kinde",
    };
  }

  // Cloudflare Access (legacy)
  const cfUser = (window as unknown as CfAccessUserGlobal).CF_ACCESS_USER;
  if (typeof cfUser === "string" && cfUser.length > 0) {
    return { email: cfUser, initials: buildInitials(cfUser), provider: "cloudflare_access" };
  }

  return { email: FALLBACK_EMAIL, initials: buildInitials(FALLBACK_EMAIL), provider: "fallback" };
};
